import {Formik} from 'formik'
import React, {Dispatch, SetStateAction, useEffect, useState} from 'react'
import {Form, Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import TextInput from '../../widgets/components/Input/TextInput'
import * as Yup from 'yup'
import {toast} from 'react-toastify'
import {useAuth} from '../../auth'
import {BasicButton} from '../../widgets/components/UI/BasicButton'
import TagManager from 'react-gtm-module'
import {GTMEvent} from '../../../hooks/gtmHelpers'
import {createOtp, verifyPhoneOtp} from '../../onboarding/core/_requests'
import {verifyToken} from '../../auth/core/_requests'
import {useThemeMode} from '../../../../_metronic/partials'
import {Link} from 'react-router-dom'

const OTPModal = ({
  showModal,
  setShowModal,
  countryCode,
  phone,
  getProfileInfo,
}: {
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
  countryCode: string
  phone: string
  getProfileInfo: any
}) => {
  const {formatMessage} = useIntl()
  const {mode} = useThemeMode()
  const [loading, setLoading] = useState(false)
  const [, setErrorsArray] = useState<string[]>()
  const [otpTimer, setOtpTimer] = useState(Number(process.env.REACT_APP_OTP_WAIT_TIME) || 20)
  const [isResendDisabled, setIsResendDisabled] = useState(false)
  const [isSendingOtp, setIsSendingOtp] = useState<boolean>(false)
  const {currentUser, companyId, setCurrentUser} = useAuth()
  const otpInitialValues = {
    otp: '',
  }
  const verifyOtpSchema = Yup.object().shape({
    otp: Yup.string()
      .matches(/^\d{6}$/, 'OTP must be a 6-digit number')
      .required('OTP is required'),
  })
  const handleOtpVerify = async (values: any) => {
    try {
      const payload: any = {
        countryDialingCode: countryCode || '',
        phoneNumber: phone || '',
        otp: values?.otp,
      }
      setLoading(true)
      const {
        data: {success, data, errors},
      } = await verifyPhoneOtp(+companyId!,payload)
      if (success) {
        if (data.status === 'approved') {
          getProfileInfo()
          toast.success(formatMessage({id: 'OTP verified'}))
          const {
            data: {success, data: value},
          } = await verifyToken(data.token)
          if (success) {
            setCurrentUser({...value})
            setShowModal(false)
          }
        } else {
          toast.success(formatMessage({id: 'OTP verification failed'}))
        }
        setLoading(false)
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.PhoneVerificationSuccessful,
            metadata: {
              user_id: currentUser?.userId,
              user_hash: currentUser?.user_hash,
              email: currentUser?.email,
              phone: `${countryCode}${phone}`,
              company: {
                company_id: companyId,
              },
            },
          },
        })
      } else {
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.PhoneVerificationFailed,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              user_hash: currentUser?.user_hash,
              email: currentUser?.email,
              phone: `${countryCode}${phone}`,
              company: {
                company_id: companyId,
              },
              error: errors,
            },
          },
        })
        setErrorsArray(errors)
        errors.forEach((error: string) => {
          toast.error(formatMessage({id: error}))
        })
      }
    } catch (error) {
      console.error(error)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.PhoneVerificationFailed,
          action: 'normal',
          metadata: {
            country_code: countryCode,
            phone_number: phone,
            full_number: `${countryCode}${phone}`,
            error: error,
          },
        },
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let countdown: any
    if (isResendDisabled) {
      countdown = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev > 1) {
            const newTime = prev - 1
            return newTime
          }
          clearInterval(countdown)
          setIsResendDisabled(false)
          return 0
        })
      }, 1000)
    }
    return () => clearInterval(countdown)
  }, [isResendDisabled])

  const startResendOtpTimer = () => {
    setIsResendDisabled(true)
  }

  const resendOtp = () => {
    startResendOtpTimer()
  }

  const formatTime = (seconds: any) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
  }

  useEffect(() => {
    if (showModal) {
      startResendOtpTimer()
    }
  }, [showModal])

  const handleResendOTP = async () => {
    try {
      const payload: any = {
        countryDialingCode: countryCode || '',
        phoneNumber: phone || '',
      }
      setIsSendingOtp(true)
      const {
        data: {success, errors},
      } = await createOtp(+companyId!,payload)
      if (success) {
        setErrorsArray([])
        setOtpTimer(Number(process.env.REACT_APP_OTP_WAIT_TIME) || 20)
        setIsSendingOtp(false)
        resendOtp()
      } else {
        errors.forEach((error: string) => {
          toast.error(formatMessage({id: error}))
        })
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsSendingOtp(false)
    }
  }
  return (
    <>
      <div>
        <Modal
          show={showModal}
          onHide={() => setShowModal(!showModal)}
          aria-labelledby='contained-modal-title-vcenter'
          centered
        >
          <Modal.Header closeButton>
            <h1 className='mb-0 text-center font-size-20'>
              {formatMessage({
                id: `Phone Verification`,
              })}
            </h1>{' '}
          </Modal.Header>
          <div>
            <div
              className='d-flex align-items-center justify-content-center h-100'
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <div className='card d-flex justify-content-center align-items-center otp-screen py-6 px-md-0 px-2 gap-6 w-100'>
                <div className='d-flex flex-column align-items-center justify-content-center gap-6 col-md-9 col-11'>
                  <div
                    className={`alert p-2 mt-4 alert-warning w-100`}
                    style={{color: '#383940', background: `${mode === 'dark' ? '#FFF8DD' : ''}`}}
                  >
                    <h1 className='font-size-13 fw-bold '>
                      {formatMessage({id: 'OTP Sent to '})}
                      {countryCode}
                      {phone}
                    </h1>
                    <p className='font-size-13 fw-semibold m-0'>
                      {formatMessage({
                        id: `Please enter the 6-digit OTP sent via SMS.`,
                      })}
                    </p>
                  </div>
                </div>
                <Formik
                  initialValues={otpInitialValues}
                  validationSchema={verifyOtpSchema}
                  onSubmit={handleOtpVerify}
                  validateOnMount
                >
                  {(formik) => (
                    <Form className='d-flex flex-column align-items-center gap-4 w-100 mb-6'>
                      <div className='col-md-9 col-11'>
                        <TextInput
                          fieldType='text'
                          label={formatMessage({id: 'Enter OTP for Verification'})}
                          fieldName='otp'
                          formik={formik}
                          placeholder={formatMessage({id: 'Enter OTP'})}
                          toolTipText={formatMessage({
                            id: 'Enter the 6-digit code sent to your phone for secure account verification.',
                          })}
                          isStarRequired={true}
                          isTooltipForModal={true}
                          onBlur={formik.handleBlur}
                        />
                      </div>
                      <div className='text-center fw-bold fs-6'>
                        <div className='text-gray-500 fw-semibold fs-6 pb-4'>
                          {formatTime(otpTimer)}
                        </div>
                        <Link
                          to='#'
                          className={`fw-bold fs-6 ${
                            isSendingOtp || otpTimer > 0 ? 'disabled text-gray-500' : 'link-color'
                          }`}
                          onClick={otpTimer === 0 ? handleResendOTP : () => {}}
                          style={{cursor: isSendingOtp ? 'default' : 'pointer'}}
                        >
                          {isSendingOtp
                            ? formatMessage({id: 'Sending. Please wait...'})
                            : formatMessage({id: 'Resend OTP'})}
                        </Link>
                      </div>
                      <BasicButton
                        buttonText={formatMessage({id: 'Verify Now'})}
                        height='44px'
                        customClass='bg-primary text-white'
                        padding='12px 24px'
                        loading={loading}
                        onClick={() => handleOtpVerify(formik.values)}
                      />
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </>
  )
}

export default OTPModal
